/**
 * UtilityProvidersTab - Presentation component for utility providers table
 *
 * This is a presentation-only component that receives all data and handlers from
 * the parent ElectricRates page. The parent manages state, filters, and dialogs.
 *
 * Features:
 * - Display providers in AdminDataTable
 * - Support for active and archived views
 * - Row actions (edit, archive, restore, delete)
 * - Bulk operations
 */

import {
  Pencil,
  Trash2,
  Archive,
  ArchiveRestore,
  Building2,
  Globe,
  ExternalLink,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { AdminDataTable, Column, Action, BulkAction } from './AdminDataTable';
import type { UtilityProvider } from '@/types/utilityProviders';
import {
  COUNTRIES,
  getRegionName,
} from '@/types/utilityProviders';
import { cn } from '@/lib/utils';

interface UtilityProvidersTabProps {
  // Data
  providers: UtilityProvider[];
  isLoading: boolean;
  viewArchived: boolean;

  // Handlers
  onEdit: (provider: UtilityProvider) => void;
  onArchive: (provider: UtilityProvider) => void;
  onHardDelete: (provider: UtilityProvider) => void;
  onRestore: (provider: UtilityProvider) => void;
  onBulkArchive: (providers: UtilityProvider[]) => void;
  onBulkRestore: (providers: UtilityProvider[]) => void;
  onBulkHardDelete: (providers: UtilityProvider[]) => void;

  // Bulk operation state
  isBulkSubmitting: boolean;
}

export function UtilityProvidersTab({
  providers,
  isLoading,
  viewArchived,
  onEdit,
  onArchive,
  onHardDelete,
  onRestore,
  onBulkArchive,
  onBulkRestore,
  onBulkHardDelete,
  isBulkSubmitting,
}: UtilityProvidersTabProps) {
  // Table columns
  const columns: Column<UtilityProvider>[] = [
    {
      key: 'name',
      label: 'Name',
      width: '280px',
      render: (provider) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-medium truncate">{provider.name}</div>
            {provider.description && (
              <div className="text-xs text-muted-foreground truncate">
                {provider.description}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'countryCode',
      label: 'Country',
      width: '130px',
      render: (provider) => (
        <Badge variant="outline" className="font-normal whitespace-nowrap">
          {COUNTRIES[provider.countryCode]}
        </Badge>
      ),
    },
    {
      key: 'regionCode',
      label: 'Region',
      width: '140px',
      render: (provider) => (
        provider.regionCode ? (
          <span className="text-sm whitespace-nowrap">
            {getRegionName(provider.countryCode, provider.regionCode)}
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        )
      ),
    },
    {
      key: 'website',
      label: 'Website',
      width: '180px',
      render: (provider) => (
        provider.website ? (
          <a
            href={provider.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm text-[#8FB560] hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <Globe className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">
              {new URL(provider.website).hostname.replace('www.', '')}
            </span>
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
          </a>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        )
      ),
    },
  ];

  // Add status column for archived view
  if (viewArchived) {
    columns.push({
      key: 'isDeleted',
      label: 'Status',
      width: '100px',
      align: 'center',
      render: (provider) => (
        <Badge
          variant={provider.isDeleted ? 'secondary' : 'default'}
          className={cn(
            'gap-1',
            provider.isDeleted && 'bg-orange-500/10 text-orange-500 border-orange-500/20'
          )}
        >
          {provider.isDeleted ? (
            <>
              <Archive className="h-3 w-3" />
              Archived
            </>
          ) : (
            'Active'
          )}
        </Badge>
      ),
    });
  }

  // Table actions
  const actions: Action<UtilityProvider>[] = viewArchived
    ? [
        {
          label: 'Restore',
          icon: ArchiveRestore,
          onClick: onRestore,
          show: (p) => p.isDeleted,
        },
        {
          label: 'Permanently Delete',
          icon: Trash2,
          onClick: onHardDelete,
          variant: 'destructive',
          show: (p) => p.isDeleted,
        },
      ]
    : [
        {
          label: 'Edit',
          icon: Pencil,
          onClick: onEdit,
        },
        {
          label: 'Archive',
          icon: Archive,
          onClick: onArchive,
          variant: 'destructive',
        },
      ];

  // Bulk actions
  const bulkActions: BulkAction<UtilityProvider>[] = viewArchived
    ? [
        {
          label: 'Restore Selected',
          icon: ArchiveRestore,
          onClick: onBulkRestore,
          disabled: isBulkSubmitting,
        },
        {
          label: 'Delete Permanently',
          icon: Trash2,
          onClick: onBulkHardDelete,
          variant: 'destructive',
          disabled: isBulkSubmitting,
        },
      ]
    : [
        {
          label: 'Archive Selected',
          icon: Archive,
          onClick: onBulkArchive,
          variant: 'destructive',
          disabled: isBulkSubmitting,
        },
      ];

  return (
    <AdminDataTable
      data={providers}
      columns={columns}
      actions={actions}
      bulkActions={bulkActions}
      onRowClick={viewArchived ? undefined : onEdit}
      emptyMessage={
        viewArchived
          ? 'No archived providers'
          : 'No providers match your filters'
      }
      loading={isLoading}
      tableId="utility-providers"
      minRows={5}
      itemLabel="provider"
    />
  );
}
